/**
 * DisplayPanelPage Component
 * TV display for showing queue numbers being called
 * Public page - no authentication required
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, CircularProgress, Typography, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useDisplayPanel, playNotificationSound } from '../../hooks/useDisplayPanel';
import {
  DisplayHeader,
  CurrentQueueNumber,
  RecentCallsList,
} from './components';

// Dark theme for TV display
const darkDisplayTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4CAF50', // Green for visibility
    },
    secondary: {
      main: '#2196F3',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    warning: {
      main: '#ff9800',
    },
    success: {
      main: '#4CAF50',
    },
    info: {
      main: '#2196F3',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflow: 'hidden',
        },
      },
    },
  },
});

// Light theme variant
const lightDisplayTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2e7d32', // Darker green for light mode
    },
    secondary: {
      main: '#1976d2',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    warning: {
      main: '#ed6c02',
    },
    success: {
      main: '#2e7d32',
    },
    info: {
      main: '#0288d1',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflow: 'hidden',
        },
      },
    },
  },
});

/**
 * Main display panel page component
 * URL Parameters:
 * - clinicId: number (required) - Clinic ID
 * - theme: 'light' | 'dark' (default: 'dark') - Initial theme
 * - showName: boolean (default: true) - Show patient name
 * - sound: boolean (default: false) - Enable notification sound
 * - refresh: number (default: 5000) - Refresh interval in ms
 */
export const DisplayPanelPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Parse URL parameters
  const clinicId = Number(searchParams.get('clinicId')) || 1;
  const date = searchParams.get('date') ?? undefined;
  const themeParam = searchParams.get('theme') ?? 'dark';
  const showName = searchParams.get('showName') !== 'false';
  const soundEnabled = searchParams.get('sound') === 'true';
  const refreshInterval = Number(searchParams.get('refresh')) || 5000;

  // Interactive theme toggle
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(themeParam === 'light' ? 'light' : 'dark');

  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Select theme
  const theme = useMemo(() => {
    return themeMode === 'light' ? lightDisplayTheme : darkDisplayTheme;
  }, [themeMode]);

  // Handle new call notification
  const handleNewCall = useCallback((queueNumber: number) => {
    console.log(`[DisplayPanel] New call: ${queueNumber}`);
    if (soundEnabled) {
      playNotificationSound();
    }
  }, [soundEnabled]);

  // Fetch display data
  const { data, loading, error, socketConnected } = useDisplayPanel({
    clinicId,
    refreshInterval,
    onNewCall: handleNewCall,
    date,
  });

  // Loading state
  if (loading && !data) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress size={80} />
          <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary' }}>
            Carregando painel...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            bgcolor: 'background.default',
            p: 4,
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            Erro ao carregar painel
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>
            Verifique se o clinicId esta correto: {clinicId}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'background.default',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <DisplayHeader clinicName={data?.clinicName} onToggleTheme={toggleTheme} themeMode={themeMode} />

        {/* Main Content — 75/25 horizontal split */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 2, minHeight: 0 }}>
          {/* Left: Current Queue Number (75%) */}
          <CurrentQueueNumber
            number={data?.currentCall?.queueNumber}
            name={showName ? data?.currentCall?.employeeName : undefined}
          />

          {/* Right: Recent Calls List (25%) */}
          <RecentCallsList calls={data?.recentCalls ?? []} />
        </Box>

        {/* Footer with last update time and connection indicator */}
        <Box
          sx={{
            pt: 1,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Chip
            label={socketConnected ? 'Ao vivo' : 'Polling'}
            size='small'
            sx={{
              bgcolor: socketConnected ? 'success.main' : 'action.disabledBackground',
              color: socketConnected ? 'success.contrastText' : 'text.disabled',
              fontSize: '0.7rem',
              height: 20,
            }}
          />
          <Typography variant="caption" color="text.disabled">
            Atualizado: {data?.lastUpdated ? new Date(data.lastUpdated).toLocaleTimeString('pt-BR') : '--:--:--'}
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DisplayPanelPage;
